import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons';

import PromotionsBetaNotice from './components/PromotionsBetaNotice';
import PromotionsPageTabs from './components/PromotionsPageTabs';
import RuleActiveToggle from './components/RuleActiveToggle';
import { CART_SUBTOTAL_CREATE_DISABLED_HINT } from '../../constants/promotionConfig';
import { usePromotionsActionButtonStyles, usePromotionsMobileStyles } from './PromotionsPage.styles';

const PromotionsPageMobile = ({
    rules,
    loading,
    onCreate,
    onCreateTier,
    onEdit,
    onDelete,
    onToggleActive,
    getRuleTypeLabel,
    getStatusChip,
    storefrontPromotionsEnabled = true,
    pageSubtitle,
    emptyStateText,
    primaryCreateLabel = 'Nueva regla',
    primaryCreateDisabled = false,
    activeTab,
    onTabChange,
    renderDiscountCell,
}) => {
    const { t } = useTranslation();
    const actionButtonClasses = usePromotionsActionButtonStyles();
    const mobileClasses = usePromotionsMobileStyles();

    const renderPrimaryCreateButton = (fullWidth = false) => {
        if (!onCreateTier) return null;

        const button = (
            <Button
                color="primary"
                variant="contained"
                size={fullWidth ? 'medium' : 'small'}
                startIcon={<AddIcon />}
                onClick={onCreateTier}
                disabled={primaryCreateDisabled}
                className={`${actionButtonClasses.actionButton} ${
                    fullWidth ? '' : mobileClasses.headerActionButton
                }`}
                fullWidth={fullWidth}
            >
                {primaryCreateLabel}
            </Button>
        );

        if (!primaryCreateDisabled) return button;

        return (
            <Tooltip title={CART_SUBTOTAL_CREATE_DISABLED_HINT}>
                <span
                    className={
                        fullWidth
                            ? mobileClasses.headerActionButtonWrapFull
                            : mobileClasses.headerActionButtonWrap
                    }
                >
                    {button}
                </span>
            </Tooltip>
        );
    };

    return (
        <Box>
            <Box className={mobileClasses.pageHeader} style={{ background: '#fff' }}>
                <Typography className={mobileClasses.pageTitle}>{t('PromotionsTitle')}</Typography>
                <Typography className={mobileClasses.pageSubtitle}>{pageSubtitle}</Typography>
                {onTabChange && (
                    <PromotionsPageTabs
                        activeTab={activeTab}
                        onChange={onTabChange}
                        compact
                    />
                )}
                <Box className={mobileClasses.headerActions}>
                    {renderPrimaryCreateButton()}
                    {onCreate && (
                        <Button
                            color="primary"
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={onCreate}
                            className={`${actionButtonClasses.actionButton} ${mobileClasses.headerActionButton}`}
                        >
                            Promoción
                        </Button>
                    )}
                </Box>
                <PromotionsBetaNotice compact />
            </Box>

            <Box className={mobileClasses.listContainer}>
                {loading && (
                    <Paper style={{ padding: 16, textAlign: 'center', borderRadius: 12 }}>
                        <Typography variant="body2">Cargando…</Typography>
                    </Paper>
                )}

                {!loading && rules.length === 0 && (
                    <Paper style={{ padding: 20, textAlign: 'center', borderRadius: 12 }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }} gutterBottom>
                            Sin reglas aún
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                            {emptyStateText}
                        </Typography>
                        {renderPrimaryCreateButton(true)}
                    </Paper>
                )}

                {!loading &&
                    rules.map((row) => (
                        <Card key={`${row.ruleKind}-${row.id}`} className={mobileClasses.ruleCard}>
                            <CardContent className={mobileClasses.ruleCardContent}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                >
                                    <Box flex={1} pr={1} minWidth={0}>
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: 600, lineHeight: 1.35 }}
                                            noWrap
                                        >
                                            {row.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            display="block"
                                            style={{ marginTop: 2 }}
                                        >
                                            {getRuleTypeLabel(row)}
                                        </Typography>
                                        <Box mt={0.75}>
                                            {renderDiscountCell(row)}
                                        </Box>
                                    </Box>
                                    <Box display="flex" flexShrink={0}>
                                        <IconButton
                                            size="small"
                                            onClick={() => onEdit(row)}
                                            aria-label="Editar"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => onDelete(row)}
                                            aria-label="Eliminar"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Box
                                    className={mobileClasses.ruleMetaRow}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                >
                                    {row.ruleKind === 'promotion' && getStatusChip(row)}
                                    <RuleActiveToggle
                                        active={row.active}
                                        onChange={() => onToggleActive(row)}
                                        disabled={
                                            row.ruleKind === 'promotion' && row.status === 'applying'
                                        }
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
            </Box>
        </Box>
    );
};

export default PromotionsPageMobile;
